import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Mail, Phone, Bot, UserX, Megaphone, ExternalLink,
  TrendingUp, Calendar, CheckCircle, Clock, MessageCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import type { BadgeVariant } from '@/components/ui/Badge';
import type { LeadApi } from '@/hooks/useLeads';

interface LeadDrawerProps {
  lead: LeadApi | null;
  open: boolean;
  onClose: () => void;
}

const classificationConfig: Record<string, {
  emoji: string;
  label: string;
  variant: BadgeVariant;
  barColor: string;
  textColor: string;
}> = {
  FRIO:       { emoji: '❄️',  label: 'Frio',       variant: 'info',    barColor: 'bg-ds-info',    textColor: 'text-ds-info' },
  MORNO:      { emoji: '🌡️', label: 'Morno',      variant: 'warning', barColor: 'bg-ds-warning', textColor: 'text-ds-warning' },
  QUENTE:     { emoji: '🔥',  label: 'Quente',     variant: 'danger',  barColor: 'bg-ds-danger',  textColor: 'text-ds-danger' },
  PRIORIDADE: { emoji: '⚡',  label: 'Prioridade', variant: 'success', barColor: 'bg-ds-success', textColor: 'text-ds-success' },
};

const statusMap: Record<string, { label: string; variant: BadgeVariant }> = {
  novo:        { label: 'Novo',        variant: 'info' },
  contatado:   { label: 'Contactado',  variant: 'warning' },
  qualificado: { label: 'Qualificado', variant: 'default' },
  convertido:  { label: 'Convertido',  variant: 'success' },
  descartado:  { label: 'Perdido',     variant: 'danger' },
};

const sourceMap: Record<string, string> = {
  website:  'Website',
  referral: 'Indicação',
  social:   'Redes Sociais',
  event:    'Evento',
};

function formatCurrency(value?: number | null): string {
  if (value == null) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold text-ds-text-secondary uppercase tracking-wider mb-3 flex items-center gap-1.5">
      {children}
    </h3>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs text-ds-text-secondary block mb-0.5">{label}</span>
      <span className="text-sm text-ds-text-primary">{value}</span>
    </div>
  );
}

export function LeadDrawer({ lead, open, onClose }: LeadDrawerProps) {
  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open || !lead) return null;

  const cls = lead.lead_classification ? classificationConfig[lead.lead_classification] : null;
  const score = lead.lead_health_score;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes de ${lead.nome}`}
        className="fixed inset-y-0 right-0 w-full sm:w-[500px] bg-ds-surface border-l border-ds-border z-50 flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-ds-border shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-ds-text-primary truncate">{lead.nome}</h2>
              <Badge variant={statusMap[lead.status]?.variant || 'default'} size="sm">
                {statusMap[lead.status]?.label || lead.status}
              </Badge>
              {cls && (
                <Badge variant={cls.variant} size="sm">
                  {cls.emoji} {cls.label}
                </Badge>
              )}
            </div>
            {lead.cargo && (
              <p className="text-sm text-ds-text-secondary mt-0.5">{lead.cargo}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-ds-text-secondary hover:text-ds-text-primary hover:bg-ds-surface2 transition-colors"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Health Score */}
          {score != null && (
            <section>
              <SectionTitle>Health Score</SectionTitle>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-2xl font-bold ${cls?.textColor || 'text-ds-text-primary'}`}>
                    {score}
                    <span className="text-sm font-normal text-ds-text-secondary">/100</span>
                  </span>
                  {cls && (
                    <span className="text-sm text-ds-text-secondary">{cls.emoji} {cls.label}</span>
                  )}
                </div>
                <div className="h-2 w-full rounded-full bg-ds-surface2 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${cls?.barColor || 'bg-ds-info'}`}
                    style={{ width: `${Math.min(score, 100)}%` }}
                  />
                </div>
                <div className="flex flex-wrap gap-4 pt-1">
                  {lead.etapa_funil && (
                    <div className="flex items-center gap-1.5 text-xs text-ds-text-secondary">
                      <TrendingUp className="h-3.5 w-3.5 shrink-0" />
                      Etapa:
                      <span className="text-ds-text-primary font-medium ml-1">{lead.etapa_funil}</span>
                    </div>
                  )}
                  {lead.calculadora_concluida != null && (
                    <div className="flex items-center gap-1.5 text-xs text-ds-text-secondary">
                      <CheckCircle className={`h-3.5 w-3.5 shrink-0 ${lead.calculadora_concluida ? 'text-ds-success' : 'text-ds-text-secondary'}`} />
                      Calculadora:
                      <span className={`font-medium ml-1 ${lead.calculadora_concluida ? 'text-ds-success' : 'text-ds-text-secondary'}`}>
                        {lead.calculadora_concluida ? 'Concluída' : 'Pendente'}
                      </span>
                    </div>
                  )}
                  {lead.agendamento_sugerido && (
                    <div className="flex items-center gap-1.5 text-xs text-ds-success">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      Agendamento sugerido
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Potencial de Captação */}
          {(lead.potencial_conservador != null || lead.potencial_realista != null || lead.potencial_ambicioso != null) && (
            <section>
              <SectionTitle>Potencial de Captação</SectionTitle>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-ds-border bg-ds-surface2 p-3 text-center">
                  <span className="text-xs text-ds-text-secondary block mb-1">Conservador</span>
                  <span className="text-sm font-semibold text-ds-text-primary">
                    {formatCurrency(lead.potencial_conservador)}
                  </span>
                </div>
                <div className="rounded-lg border border-ds-success/40 bg-ds-success/5 p-3 text-center ring-1 ring-ds-success/20">
                  <span className="text-xs text-ds-success block mb-1">Realista</span>
                  <span className="text-base font-bold text-ds-success">
                    {formatCurrency(lead.potencial_realista)}
                  </span>
                </div>
                <div className="rounded-lg border border-ds-border bg-ds-surface2 p-3 text-center">
                  <span className="text-xs text-ds-text-secondary block mb-1">Ambicioso</span>
                  <span className="text-sm font-semibold text-ds-text-primary">
                    {formatCurrency(lead.potencial_ambicioso)}
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* Contato */}
          <section>
            <SectionTitle>Contato</SectionTitle>
            <div className="space-y-2.5">
              {lead.email && (
                <div className="flex items-center gap-2.5 text-sm">
                  <Mail className="h-4 w-4 shrink-0 text-ds-text-secondary" />
                  <a
                    href={`mailto:${lead.email}`}
                    className="text-ds-text-primary hover:text-ds-success transition-colors truncate"
                  >
                    {lead.email}
                  </a>
                </div>
              )}
              {lead.telefone && (
                <div className="flex items-center gap-2.5 text-sm">
                  <Phone className="h-4 w-4 shrink-0 text-ds-text-secondary" />
                  <a
                    href={`tel:${lead.telefone}`}
                    className="text-ds-text-primary hover:text-ds-success transition-colors"
                  >
                    {lead.telefone}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-2.5 text-sm">
                {lead.ia_ativo ? (
                  <>
                    <Bot className="h-4 w-4 shrink-0 text-ds-success" />
                    <span className="text-ds-success">IA ativa</span>
                  </>
                ) : (
                  <>
                    <UserX className="h-4 w-4 shrink-0 text-ds-danger" />
                    <span className="text-ds-danger">IA desativada</span>
                  </>
                )}
              </div>
              {lead.responsavel_nome && (
                <div className="flex items-center gap-2.5 text-sm">
                  <span className="text-ds-text-secondary">Responsável:</span>
                  <span className="text-ds-text-primary">{lead.responsavel_nome}</span>
                </div>
              )}
            </div>
          </section>

          {/* Conversa WhatsApp (unificada com Assistente IA) */}
          {lead.conversa && (
            <section>
              <SectionTitle>
                <MessageCircle className="h-3.5 w-3.5" />
                Conversa WhatsApp
              </SectionTitle>
              <div className="rounded-lg border border-ds-border bg-ds-surface2/50 p-4 space-y-2.5">
                <div className="flex items-center gap-2.5 text-sm">
                  <Phone className="h-4 w-4 shrink-0 text-ds-text-secondary" />
                  <span className="text-ds-text-primary">
                    {lead.conversa.numero_real || lead.conversa.channel_id}
                  </span>
                </div>
                {lead.conversa.lid && (
                  <div className="text-xs text-ds-text-secondary">
                    LID: <span className="font-mono">{lead.conversa.lid}</span>
                  </div>
                )}
                <div className="flex items-center gap-2.5 text-sm">
                  <Bot className={`h-4 w-4 shrink-0 ${lead.conversa.is_ai_disabled ? 'text-ds-danger' : 'text-ds-success'}`} />
                  <span className={lead.conversa.is_ai_disabled ? 'text-ds-danger' : 'text-ds-success'}>
                    {lead.conversa.is_ai_disabled ? 'IA desativada nesta conversa' : 'IA ativa nesta conversa'}
                  </span>
                </div>
                {lead.conversa.disabled_since && (
                  <div className="text-xs text-ds-text-secondary">
                    Desde: {new Date(lead.conversa.disabled_since).toLocaleDateString('pt-BR')}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Empresa & Qualificação */}
          {(lead.empresa || lead.segmento || lead.faturamento_mensal_faixa || lead.cnpj_maturidade ||
            lead.regularidade_fiscal || lead.experiencia_licitacoes || lead.decisor || lead.urgencia_meses) && (
            <section>
              <SectionTitle>Empresa &amp; Qualificação</SectionTitle>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Empresa" value={lead.empresa} />
                <Field label="Segmento" value={lead.segmento} />
                <Field label="Faturamento Mensal" value={lead.faturamento_mensal_faixa} />
                <Field label="Maturidade CNPJ" value={lead.cnpj_maturidade} />
                <Field label="Regularidade Fiscal" value={lead.regularidade_fiscal} />
                <Field label="Experiência em Licitações" value={lead.experiencia_licitacoes} />
                <Field label="Decisor" value={lead.decisor} />
                <Field label="Urgência (meses)" value={lead.urgencia_meses} />
              </div>
            </section>
          )}

          {/* Dados do Anúncio */}
          {lead.is_trafego_pago && (
            <section>
              <SectionTitle>
                <Megaphone className="h-3.5 w-3.5" />
                Dados do Anúncio
              </SectionTitle>
              <div className="rounded-lg border border-ds-border bg-ds-surface2/50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <Field label="Título" value={lead.ad_title} />
                  <Field label="Plataforma" value={lead.ad_source_app} />
                  <Field label="Tipo de Mídia" value={lead.ad_media_type} />
                  <Field label="Source Type" value={lead.ad_source_type} />
                  {lead.ad_source_id && (
                    <div className="col-span-2">
                      <span className="text-xs text-ds-text-secondary block mb-0.5">ID do Anúncio</span>
                      <span className="text-xs font-mono text-ds-text-primary">{lead.ad_source_id}</span>
                    </div>
                  )}
                  {lead.ad_ctwa_clid && (
                    <div className="col-span-2">
                      <span className="text-xs text-ds-text-secondary block mb-0.5">CTWA Click ID</span>
                      <span className="text-xs font-mono text-ds-text-primary break-all">{lead.ad_ctwa_clid}</span>
                    </div>
                  )}
                </div>
                {lead.ad_body && (
                  <div>
                    <span className="text-xs text-ds-text-secondary block mb-1">Texto do Anúncio</span>
                    <p className="text-sm text-ds-text-primary bg-ds-surface rounded p-2 whitespace-pre-line">
                      {lead.ad_body}
                    </p>
                  </div>
                )}
                {(lead.ad_source_url || lead.ad_media_url) && (
                  <div className="flex items-center gap-4 pt-1">
                    {lead.ad_source_url && (
                      <a
                        href={lead.ad_source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-ds-success hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Ver anúncio
                      </a>
                    )}
                    {lead.ad_media_url && (
                      <a
                        href={lead.ad_media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-ds-success hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Ver mídia
                      </a>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Origem */}
          <section>
            <SectionTitle>Origem</SectionTitle>
            <div className="flex items-start gap-8 text-sm">
              <div>
                <span className="text-xs text-ds-text-secondary block mb-0.5">Fonte</span>
                <span className="text-ds-text-primary capitalize">
                  {lead.is_trafego_pago
                    ? 'Tráfego Pago'
                    : (sourceMap[lead.fonte || ''] || lead.fonte || 'Outro')}
                </span>
              </div>
              <div>
                <span className="text-xs text-ds-text-secondary block mb-0.5">Cadastrado em</span>
                <span className="text-ds-text-primary flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-ds-text-secondary shrink-0" />
                  {formatDate(lead.created_at)}
                </span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </>,
    document.body
  );
}
